import type { DashboardMetricType } from './components/AddToDashboard/AddToDashboard';
import {
  DataColorsType,
  DataType,
  InsightsFilterType,
  QueryFilters,
  TypeDuration,
  TypeTimeInterval,
} from './Insights.types';
import type { VCPUProductType } from './pages/InsightsExploreUtilization/InsightsExploreUtilization.types';

export const INFRA_VCPU_PRODUCTS: VCPUProductType[] = ['build_hub', 'rde'];
export const INFRA_VCPU_PRODUCT_LABELS: Record<VCPUProductType, string> = {
  build_hub: 'Build Hub',
  rde: 'Remote Dev Environments',
};
export const INFRA_VCPU_PRODUCT_COLORS: DataColorsType = {
  build_hub: { css: 'var(--colors-color\\/turquoise\\/bold)', name: 'color/turquoise/bold' },
  rde: { css: 'var(--colors-color\\/orange\\/bold)', name: 'color/orange/bold' },
};

export const TIME_INTERVAL_DEFAULT: TypeTimeInterval = 'weekly';
export const TIME_RANGE_DEFAULT: TypeDuration = 'P12W';

export const RELATED_INVOCATIONS_NOTIFICATION_CLOSED_COOKIE = 'hide_related_invocations_notification';

export const TIME_INTERVAL: { text: string; value: TypeTimeInterval }[] = [
  {
    text: 'Hourly',
    value: 'hourly',
  },
  {
    text: 'Daily',
    value: 'daily',
  },
  {
    text: 'Weekly',
    value: 'weekly',
  },
  {
    text: 'Monthly',
    value: 'monthly',
  },
];

export const TIME_INTERVAL_PRESETS: {
  [key in TypeTimeInterval]: {
    text: string;
    value: TypeDuration;
    default?: boolean;
  }[];
} = {
  daily: [
    {
      text: 'Last 7 days',
      value: 'P7D',
    },
    {
      text: 'Last 14 days',
      value: 'P14D',
    },
    {
      default: true,
      text: 'Last 30 days',
      value: 'P30D',
    },
    {
      text: 'Last 45 days',
      value: 'P45D',
    },
    {
      text: 'Last 60 days',
      value: 'P60D',
    },
    {
      text: 'Last 90 days',
      value: 'P90D',
    },
  ],

  hourly: [
    {
      default: true,
      text: 'Last 1 day',
      value: 'P1D',
    },
    {
      text: 'Last 2 days',
      value: 'P2D',
    },
    {
      text: 'Last 3 days',
      value: 'P3D',
    },
    {
      text: 'Last 5 days',
      value: 'P5D',
    },
    {
      text: 'Last 7 days',
      value: 'P7D',
    },
  ],

  secondly: [],

  monthly: [
    {
      default: true,
      text: 'Last 6 months',
      value: 'P6M',
    },
    {
      text: 'Last 12 months',
      value: 'P12M',
    },
    {
      text: 'Last 18 months',
      value: 'P18M',
    },
    {
      text: 'Last 24 months',
      value: 'P24M',
    },
  ],

  weekly: [
    {
      default: true,
      text: 'Last 12 weeks',
      value: 'P12W',
    },
    {
      text: 'Last 24 weeks',
      value: 'P24W',
    },
    {
      text: 'Last 52 weeks',
      value: 'P52W',
    },
    {
      text: 'Last 72 weeks',
      value: 'P72W',
    },
  ],
};

export const INSIGHTS_DURATION_KEY = 'insightsDuration';
export const INSIGHTS_INTERVAL_KEY = 'insightsInterval';
export const INSIGHTS_DATE_FROM_KEY = 'insightsDateFrom';
export const INSIGHTS_DATE_TO_KEY = 'insightsDateTo';

export const LIMITS = {
  alerts: 100,
  dashboards: 100,
  widgets: 30,
};

export const DATA_TYPES: Record<DataType, string> = {
  builds: 'Builds',
  tests: 'Tests',
  credits: 'Utilization',
  commands: 'Commands',
  build_cache: 'Build Cache',
  git: 'Git',
  pipelines: 'Pipelines',
};

export const METRIC_TYPES: Record<DashboardMetricType, string> = {
  total_ai_credits: 'AI Credits',
  build_hub_machine_minutes: 'Build Hub machine run minutes per machine types',
  rbe_machine_minutes: 'RBE machine run minutes per machine types',
  artifact_storage: 'Artifact storage',
  cache_hit_rate_p10: 'P10 Cache hit rate',
  cache_hit_rate_p50: 'P50 Cache hit rate',
  code_push_active_users: 'CodePush active users',
  code_push_downloads: 'CodePush downloads',
  code_push_data_transfer: 'CodePush data transfer',
  concurrencies: 'Concurrencies',
  ci_vcpu_concurrencies: 'vCPU concurrencies',
  ci_vcpu_concurrencies_p90: 'Top vCPU concurrencies (p90)',
  ci_vcpu_concurrencies_p50: 'Typical vCPU concurrencies (p50)',
  ci_vcpu_concurrencies_peak: 'Peak vCPU concurrencies',
  infrastructure_vcpu_concurrencies: 'Infrastructure vCPU concurrencies',
  infrastructure_vcpu_concurrencies_p90: 'Top infrastructure vCPU concurrencies (p90)',
  infrastructure_vcpu_concurrencies_p50: 'Typical infrastructure vCPU concurrencies (p50)',
  infrastructure_vcpu_concurrencies_peak: 'Peak infrastructure vCPU concurrencies',
  concurrencies_p50: 'Typical concurrencies (p50)',
  concurrencies_p90: 'Top concurrencies (p90)',
  concurrencies_peak: 'Peak concurrencies',
  cycle_time: 'Cycle time',
  duration_p50: 'P50 Duration',
  duration_p90: 'P90 Duration',
  error_rate: 'Error rate',
  failure_rate: 'Failure rate',
  flaky_runs: 'Flaky runs',
  invocation_count: 'Invocation count',
  infrastructure_cost: 'Infrastructure budget',
  merge_frequency: 'Merge frequency',
  network_egress: 'Network egress',
  queues: 'Queued builds',
  time_success_p50: 'Successful time (p50)',
  time_success_p90: 'Successful time (p90)',
  total_count: 'Total count',
  total_credits: 'Total credits',
  total_duration: 'Total duration',
  total_runs: 'Total runs',
  total_time: 'Total time',
  transfer_p50: 'P50 Uploads/downloads',
  transfer_p90: 'P90 Uploads/downloads',
  workflow_runs: 'Workflow runs',
};

export const FILTERS: Record<keyof QueryFilters, string> = {
  project_id: 'Project',
  branch: 'Branch',
  build_tool: 'Tool',
  build: 'Build',
  ci_provider: 'CI provider',
  command: 'Command',
  hostname: 'Hostname',
  machine_type: 'Machine type',
  module: 'Module',
  pipeline: 'Pipeline',
  stack: 'Stack',
  stage: 'Stage',
  status: 'Status',
  step: 'Step',
  step_status: 'Step status',
  target_branch: 'Target branch',
  test_case_name: 'Test case',
  test_suite_name: 'Test suite',
  username: 'Username',
  workflow: 'Workflow',
};

export const FILTERS_PLURAL: Record<keyof QueryFilters, string> = {
  project_id: 'Projects',
  branch: 'Branches',
  build_tool: 'Tools',
  build: 'Builds',
  ci_provider: 'CI providers',
  command: 'Commands',
  hostname: 'Hostnames',
  machine_type: 'Machine types',
  module: 'Modules',
  pipeline: 'Pipelines',
  stack: 'Stacks',
  stage: 'Stages',
  status: 'Statuses',
  step: 'Steps',
  step_status: 'Step statuses',
  target_branch: 'Target branches',
  test_case_name: 'Test cases',
  test_suite_name: 'Test suites',
  username: 'Usernames',
  workflow: 'Workflows',
};

export const BREAKDOWN_TYPES = {
  apps: 'projects',
  branches: 'branches',
  build_tools: 'tools',
  builds: 'builds',
  ci_providers: 'CI providers',
  commands: 'commands',
  hostnames: 'hostnames',
  machine_types: 'machine types',
  pipelines: 'pipelines/workflows',
  projects: 'projects',
  rm_apps: 'rm_apps',
  stacks: 'stacks',
  stages: 'stages',
  steps: 'steps',
  target_branches: 'target branches',
  tasks: 'tasks',
  test_suites: 'test suites',
  usernames: 'usernames',
  workflows: 'workflows',
};

export const BREAKDOWNS_TO_FILTERS: Record<keyof typeof BREAKDOWN_TYPES, string> = {
  apps: 'project_id',
  branches: 'branch',
  build_tools: 'tool',
  builds: 'build',
  ci_providers: 'ci_provider',
  hostnames: 'hostname',
  commands: 'command',
  machine_types: 'machine_type',
  pipelines: 'pipeline',
  projects: 'project_id',
  rm_apps: 'rm_app_id',
  stacks: 'stack',
  stages: 'stage',
  steps: 'step',
  target_branches: 'target_branch',
  tasks: 'task',
  test_suites: 'test_suite_name',
  usernames: 'username',
  workflows: 'workflow',
};

export const FILTER_KEYS: Record<InsightsFilterType, string[]> = {
  bottlenecks: ['workflow'],
  builds: ['pipeline', 'stage', 'workflow', 'branch', 'machine_type', 'stack', 'step', 'step_status'],
  commands: [
    'pipeline',
    'stage',
    'workflow',
    'hostname',
    'username',
    'ci_provider',
    'branch',
    'machine_type',
    'stack',
    'step',
    'command',
    'build',
  ],
  build_cache: [
    'pipeline',
    'stage',
    'workflow',
    'hostname',
    'username',
    'ci_provider',
    'branch',
    'machine_type',
    'stack',
    'step',
    'command',
    'build',
  ],
  credits: ['pipeline', 'stage', 'workflow', 'hostname', 'username', 'ci_provider'],
  git: ['target_branch'],
  pipelines: ['pipeline', 'branch'],
  tests: ['pipeline', 'stage', 'workflow', 'test_suite_name', 'module', 'test_case_name', 'branch'],
};
